// The RAISE design system. A single shared foundation (typography, shape, shadows,
// and modern MUI component defaults) is applied to BOTH palettes; only the colors
// differ by network origin (is_uta_network):
//   - RAISE (neutral, default)  -> deep ocean (teal-navy) + gold
//   - UTA (brand)               -> navy + orange
// Keep the brand hex values here in sync with src/styles/_theme.scss.
import { createTheme } from "@mui/material/styles";

// The stack itself lives in src/index.scss (--app-font), so CSS and MUI cannot drift apart.
const FONT_STACK = "var(--app-font)";
const HEADING_STACK = "var(--app-font-heading)";

// Soft, layered elevations - modern and quiet, vs MUI's harder default shadows.
const SHADOW_SM = "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)";
const SHADOW_MD = "0 2px 8px rgba(16,24,40,0.06), 0 8px 24px rgba(16,24,40,0.08)";

// Builds a full theme from just a palette, layering the shared foundation on top.
function makeTheme(palette) {
  return createTheme({
    palette,
    shape: { borderRadius: 6 },
    typography: {
      fontFamily: FONT_STACK,
      h1: { fontFamily: HEADING_STACK, fontWeight: 700, letterSpacing: "-0.02em" },
      h2: { fontFamily: HEADING_STACK, fontWeight: 700, letterSpacing: "-0.01em" },
      h3: { fontFamily: HEADING_STACK, fontWeight: 700, letterSpacing: "-0.01em" },
      h4: { fontFamily: HEADING_STACK, fontWeight: 700 },
      h5: { fontFamily: HEADING_STACK, fontWeight: 600 },
      h6: { fontFamily: HEADING_STACK, fontWeight: 600 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600, textTransform: "none" },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 4, paddingInline: 18 },
        },
      },
      MuiPaper: {
        styleOverrides: { rounded: { borderRadius: 6 } },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 6,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: SHADOW_SM,
          }),
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: "none" } },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: "none", fontWeight: 600, minHeight: 48 } },
      },
      MuiOutlinedInput: {
        styleOverrides: { root: { borderRadius: 4 } },
      },
      MuiChip: {
        styleOverrides: { root: { fontWeight: 600 } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { fontSize: "0.8rem", borderRadius: 4, padding: "6px 10px" },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: ({ theme }) => ({
            fontWeight: 700,
            color: theme.palette.text.secondary,
          }),
        },
      },
      MuiMenu: {
        styleOverrides: { paper: { borderRadius: 6, boxShadow: SHADOW_MD } },
      },
    },
  });
}

// RAISE - neutral, presentable identity for non-UTA visitors (deep ocean teal-navy + gold).
export const raiseTheme = makeTheme({
  primary: { main: "#0E7490", dark: "#0C4A6E", contrastText: "#ffffff" },
  secondary: { main: "#FBBF24", contrastText: "#1f2a37" },
  error: { main: "#d70825" },
  background: { default: "#F1F7FB", paper: "#ffffff" },
  text: { primary: "#122E3A", secondary: "#52606b" },
});

// UTA - official brand palette, shown only to UTA-network visitors (navy + orange).
export const utaTheme = makeTheme({
  primary: { main: "#0064B1", dark: "#0A3A66", contrastText: "#ffffff" },
  secondary: { main: "#F58025", contrastText: "#ffffff" },
  error: { main: "#d70825" },
  background: { default: "#F4F8F9", paper: "#ffffff" },
  text: { primary: "#1A2733", secondary: "#55636e" },
});

export const themeFor = (isUta) => (isUta ? utaTheme : raiseTheme);
